import { PriceAndReviewsUI } from "./PriceAndReviewsUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "../../hooks/useVariant/useVariant";
import {
  Display,
  Price,
  PriceRange,
  ReductionHistory,
  ReductionHistoryRange,
} from "../../types/prodcatBaseTypes";
import { postMonetateImpression } from "../../utils/monetate/postMonetateImpression";
import { Experience } from "../../types/types";
import { Buffer } from "buffer";
import { logger } from "../../utils/logger/logger";
import { isPerMetre } from "../../utils/product/isPerMetre";
import { getColourGroupForVariant } from "../../utils/product/getColourGroupForVariant";

type VariantGroupPriceRange = {
  __typename?: "PriceRange";
  display: Display;
  reductionHistory: Array<ReductionHistoryRange>;
};

function getPriceValueOrRange(
  priceData: { max: string; min: string } | string | undefined,
) {
  if (typeof priceData === "object") {
    return priceData.max === priceData.min
      ? priceData.min
      : {
          from: priceData.min,
          to: priceData.max,
        };
  }
  return priceData || "";
}

function getReductionHistoryItem(
  reductionHistory: Array<ReductionHistoryRange | ReductionHistory>,
  chronology: number,
) {
  const historyItem = reductionHistory?.find(
    (item) => item.chronology === chronology,
  );
  return getPriceValueOrRange(historyItem?.display);
}

function convertVariantPriceRangeToFormattedPrice(
  variantPriceRange: PriceRange | Price | VariantGroupPriceRange | undefined,
) {
  return variantPriceRange
    ? {
        was: getReductionHistoryItem(variantPriceRange.reductionHistory, 0),
        then1: getReductionHistoryItem(variantPriceRange.reductionHistory, 1),
        then2: getReductionHistoryItem(variantPriceRange.reductionHistory, 2),
        now: getPriceValueOrRange(variantPriceRange.display),
      }
    : undefined;
}

export function PriceAndReviews({
  product,
  experiences,
  experiencesRawString,
  runtimeConfig,
  configProps,
}: Pick<
  StandardProps,
  | "product"
  | "experiences"
  | "experiencesRawString"
  | "runtimeConfig"
  | "configProps"
>) {
  const { variantPriceRange, reviewSummary } = product;
  const { variant, isSelected } = useVariant(product);
  const wrapOnMobile = Boolean(configProps?.wrapOnMobile);

  let priceRange: PriceRange | Price | VariantGroupPriceRange | undefined;

  if (configProps?.useVariantGroups && !isSelected) {
    const variantGroup = getColourGroupForVariant(
      product.variantGroups,
      variant.id,
    );
    const variantGroupPriceRange = variantGroup?.variantPriceRange;

    priceRange = variantGroupPriceRange || variantPriceRange;
  } else {
    priceRange = isSelected ? variant.price : variantPriceRange;
  }

  const brand = product.brand?.name || "";
  const formattedPrice = convertVariantPriceRangeToFormattedPrice(priceRange);

  const priceSuffix = isPerMetre(product) ? "per metre" : undefined;

  const isNowWas = experiences["now-was"] === "on";
  //need impressionId from experience - temporary solution here until WA-561 resolved
  let nowWasExperience;

  try {
    if (experiencesRawString) {
      const decodedExperiences = Buffer.from(
        experiencesRawString,
        "base64",
      ).toString("utf8");
      const detailedExperiences = JSON.parse(decodedExperiences);
      nowWasExperience = detailedExperiences.find((experience: Experience) => {
        if (experience.parameters) {
          return Object.prototype.hasOwnProperty.call(
            experience.parameters,
            "pdp-scaffold-now-was",
          );
        } else {
          return false;
        }
      });
    }
  } catch (error) {
    logger(
      "Error in PriceAndReviews component when parsing experiencesRawString",
      {
        experiencesRawString: experiencesRawString,
      },
    );

    return (
      <PriceAndReviewsUI
        wrapOnMobile={wrapOnMobile}
        price={formattedPrice}
        priceSuffix={priceSuffix}
        brand={brand}
        rating={
          reviewSummary && {
            averageRating: reviewSummary.averageRating,
            reviews: reviewSummary.numberOfReviews,
          }
        }
        isNowWas={false}
      />
    );
  }

  const hasReductionHistory =
    formattedPrice?.was || formattedPrice?.then1 || formattedPrice?.then2;

  const monetateEnvironment = runtimeConfig.monetateEnvironment;

  if (
    monetateEnvironment &&
    hasReductionHistory &&
    nowWasExperience &&
    nowWasExperience.impressionId
  ) {
    postMonetateImpression(nowWasExperience.impressionId, monetateEnvironment);
  }

  return (
    <PriceAndReviewsUI
      wrapOnMobile={wrapOnMobile}
      price={formattedPrice}
      priceSuffix={priceSuffix}
      brand={brand}
      rating={
        reviewSummary && {
          averageRating: reviewSummary.averageRating,
          reviews: reviewSummary.numberOfReviews,
        }
      }
      isNowWas={isNowWas}
    />
  );
}
